import moment from "moment";

export const getEventId = (event = {}) => (
  event?.id ??
  event?.Id ??
  event?.SourceId ??
  event?.sourceId ??
  event?.source_id ??
  event?.calendar_event_id ??
  event?.calendarEventId ??
  event?.event_id ??
  event?.eventId ??
  event?.ticket_id ??
  event?.leave_ticket_id ??
  event?.time_entry_id ??
  null
);

export const getEventTitle = (event = {}) => (
  event?.Subject ||
  event?.title ||
  event?.name ||
  event?.eventName ||
  event?.upcomingEventName ||
  "Untitled"
);

export const getCleanEventTitle = (event = {}) => {
  const rawTitle = String(getEventTitle(event)).trim();
  return rawTitle.replace(/^Reminder\s*[-:]\s*/i, "").trim() || rawTitle;
};

export const getCategory = (event = {}) => (
  String(event?.category || event?.Category || event?.categoryName || "event")
    .trim()
    .toLowerCase()
);

export const getEventOwnerId = (event = {}) => (
  event?.user_id ??
  event?.userId ??
  event?.UserId ??
  event?.created_by ??
  event?.createdBy ??
  event?.employee_id ??
  event?.employeeId ??
  event?.created_user_id ??
  event?.resource?.user_id ??
  event?.resource?.created_by ??
  null
);

export const parseTargetUserIds = (event = {}) => {
  const desc = String(event?.description || event?.Description || event?.details || event?.Details || event?.resource?.description || event?.resource?.Description || "");
  const embeddedMatch = desc.match(/\[targets:([^\]]+)\]/i) || desc.match(/\[target_users:([^\]]+)\]/i);
  if (embeddedMatch && embeddedMatch[1]) {
    const ids = embeddedMatch[1].split(",").map((id) => id.trim()).filter(Boolean);
    if (ids.length > 0) return ids;
  }

  const rawTargetUsers =
    event?.target_user_ids ??
    event?.TargetUserIds ??
    event?.targetUserIds ??
    event?.target_users ??
    event?.targetUsers ??
    event?.user_ids ??
    event?.userIds ??
    event?.users ??
    event?.people ??
    event?.assigned_to ??
    event?.assignedTo ??
    event?.assigned_users ??
    event?.assignedUsers ??
    event?.participants ??
    event?.members ??
    event?.resource?.target_user_ids ??
    event?.resource?.TargetUserIds ??
    event?.resource?.targetUserIds ??
    event?.resource?.target_users ??
    event?.resource?.targetUsers ??
    event?.resource?.user_ids ??
    event?.resource?.userIds ??
    event?.resource?.users ??
    event?.resource?.people ??
    event?.resource?.assigned_to ??
    event?.resource?.assignedTo ??
    event?.resource?.assigned_users ??
    event?.resource?.assignedUsers ??
    event?.resource?.participants ??
    event?.resource?.members ??
    [];

  if (typeof rawTargetUsers === "number") {
    return [String(rawTargetUsers)];
  }

  if (Array.isArray(rawTargetUsers)) {
    return rawTargetUsers
      .map((id) => {
        if (typeof id === "object" && id !== null) {
          return String(id.id ?? id.value ?? id.userId ?? id.user_id ?? "");
        }
        return String(id);
      })
      .filter(Boolean);
  }

  if (typeof rawTargetUsers === "string") {
    const trimmed = rawTargetUsers.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((id) => {
            if (typeof id === "object" && id !== null) {
              return String(id.id ?? id.value ?? id.userId ?? id.user_id ?? "");
            }
            return String(id);
          })
          .filter(Boolean);
      }
      if (typeof parsed === "number" || typeof parsed === "string") {
        return [String(parsed)];
      }
    } catch (error) {
      // Fallback for non-JSON strings
    }
    return trimmed
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }

  return [];
};

export const getShowToAll = (event = {}) => {
  const rawValue =
    event?.showToAll ??
    event?.show_to_all ??
    event?.ShowToAll ??
    event?.show_all ??
    event?.is_show_all ??
    event?.resource?.showToAll ??
    event?.resource?.show_to_all;

  if (typeof rawValue === "boolean") {
    return rawValue;
  }

  if (typeof rawValue === "number") {
    return rawValue === 1;
  }

  if (typeof rawValue === "string") {
    const normalizedValue = rawValue.trim().toLowerCase();
    if (["1", "true", "yes"].includes(normalizedValue)) {
      return true;
    }
    if (["0", "false", "no"].includes(normalizedValue)) {
      return false;
    }
  }

  return false;
};

export const isMeetingEvent = (event = {}, category = getCategory(event)) => {
  if (category === "meeting") {
    return true;
  }

  if (["reminder", "calendar", "title", "event"].includes(category)) {
    const rawTitle = String(getEventTitle(event)).toLowerCase();
    return (
      rawTitle.startsWith("meeting") ||
      rawTitle.includes("meeting -") ||
      rawTitle.includes("reminder - meeting") ||
      rawTitle.includes(" scrum") ||
      rawTitle.startsWith("scrum")
    );
  }

  return false;
};

export const getEffectiveCategory = (event = {}) => {
  const category = getCategory(event);
  return isMeetingEvent(event, category) ? "meeting" : category;
};

export const getEventStartMoment = (event = {}, treatAsAllDay = false) => {
  const rawValue =
    event?.startDate ||
    event?.start_date ||
    event?.StartTime ||
    event?.startTime ||
    event?.start ||
    event?.date ||
    event?.eventdate ||
    event?.event_date ||
    "";

  if (!rawValue) {
    return moment.invalid();
  }

  const directMoment = moment(rawValue);
  if (directMoment.isValid()) {
    return treatAsAllDay ? directMoment.startOf("day") : directMoment;
  }

  return moment(rawValue, [
    "YYYY-MM-DD h:mm A",
    "YYYY-MM-DD hh:mm A",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "DD-MM-YYYY",
    "MM-DD-YYYY",
  ], true);
};

export const getLeaveOwnerName = (event = {}) => (
  event?.firstName ||
  event?.userName ||
  event?.employeeName ||
  event?.employee_name ||
  null
);

export const formatMeetingTitle = (event = {}) => {
  const cleanedTitle = getCleanEventTitle(event);
  if (/^meeting\s*[-:]/i.test(cleanedTitle)) {
    return cleanedTitle;
  }
  return `Meeting - ${cleanedTitle}`;
};

export const isMeetingRecipient = (event = {}, { isAdmin = false, currentUserId = null } = {}) => {
  if (isAdmin) {
    return true;
  }

  // Non-admin employees: show reminders for employee-created meetings as well
  const ownerId = getEventOwnerId(event);
  if (ownerId !== null && currentUserId !== null && String(ownerId) === String(currentUserId)) {
    return true;
  }

  const showToAll = getShowToAll(event);
  if (showToAll) {
    return true;
  }

  const targetUserIds = parseTargetUserIds(event);
  if (targetUserIds.length === 0) {
    return false;
  }

  return currentUserId !== null && targetUserIds.includes(String(currentUserId));
};

export const getLeaveStatus = (event = {}) => {
  const rawStatus = String(
    event?.LeaveStatus ||
    event?.leaveStatus ||
    event?.leave_status ||
    event?.Status ||
    event?.status ||
    event?.resource?.LeaveStatus ||
    event?.resource?.leaveStatus ||
    event?.resource?.Status ||
    event?.resource?.status ||
    ""
  ).toLowerCase().trim();

  const titleLower = String(getEventTitle(event)).toLowerCase();

  if (rawStatus.includes("pending") || titleLower.includes("pending")) {
    return "pending";
  }
  if (rawStatus.includes("declin") || titleLower.includes("declin")) {
    return "declined";
  }
  if (rawStatus.includes("unapprov") || titleLower.includes("unapprov")) {
    return "unapproved";
  }
  if (rawStatus.includes("cancel") || titleLower.includes("cancel")) {
    return "cancelled";
  }
  if (rawStatus.includes("approve") || titleLower.includes("approve")) {
    return "approved";
  }

  return "approved";
};

export const getStableEventKey = (event = {}) => {
  const rawId = getEventId(event);
  if (rawId !== null && typeof rawId !== "undefined") {
    const strId = String(rawId).trim();
    const match = strId.match(/^[a-z]+-(\d+)-/i);
    if (match) {
      return match[1];
    }
    return strId;
  }
  const cleanTitle = getCleanEventTitle(event).toLowerCase().replace(/[^a-z0-9]/g, "");
  const startMoment = getEventStartMoment(event, false);
  const startStr = startMoment.isValid() ? startMoment.format("YYYYMMDDHHmm") : "";
  return `${cleanTitle}_${startStr}`;
};

export const isTimerEntry = (event = {}) => {
  if (!event) return false;
  const resource = event.resource || event;

  const isTimerFlag =
    resource.is_timer === 1 ||
    resource.is_timer === true ||
    resource.is_timer === "1" ||
    resource.is_timer_entry === true ||
    resource.is_timer_entry === 1 ||
    resource.type === "timer" ||
    resource.entry_type === "timer" ||
    resource.source === "timer" ||
    resource.is_clock_in === 1 ||
    resource.is_clock_in === true ||
    resource.is_clock_out === 1 ||
    resource.is_clock_out === true ||
    resource.is_auto === 1 ||
    resource.is_auto === true;

  const rawTitle = String(
    resource.title || resource.Title || resource.Subject || resource.name || ""
  ).toLowerCase().trim();

  const isTimerTitle =
    rawTitle.includes("timer") ||
    rawTitle.includes("clock in") ||
    rawTitle.includes("clock out") ||
    rawTitle === "in/out" ||
    rawTitle === "in-out" ||
    rawTitle === "intimedata" ||
    rawTitle === "outtimedata";

  const rawDesc = String(
    resource.reason ||
    resource.entry_reason ||
    resource.time_entry_reason ||
    resource.description ||
    resource.Description ||
    resource.remark ||
    ""
  ).replace(/^Reason:\s*/i, "").trim().toLowerCase();

  const isTimerDesc =
    !rawDesc ||
    rawDesc === "none" ||
    rawDesc === "null" ||
    rawDesc === "undefined" ||
    rawDesc === "intimedata" ||
    rawDesc === "outtimedata" ||
    rawDesc === "timer in/out" ||
    rawDesc === "timer in" ||
    rawDesc === "timer out" ||
    rawDesc === "clock in" ||
    rawDesc === "clock out";

  const isApprovedVal = resource.is_approved ?? resource.IsApproved ?? resource.isApproved;
  const isApprovedStr = String(isApprovedVal ?? "").trim().toLowerCase();

  return (
    isTimerFlag ||
    isTimerTitle ||
    (isTimerDesc && (isApprovedStr === "0" || isApprovedStr === "approved"))
  );
};

export const isUserActive = (user = {}) => {
  if (!user || typeof user !== "object") return false;

  const userLogged = user.user_logged ?? user.userLogged;
  if (userLogged !== undefined && userLogged !== null) {
    if (userLogged === 0 || userLogged === "0" || userLogged === false) return false;
    if (userLogged === 1 || userLogged === "1" || userLogged === true) return true;
  }

  const uStatus = user.user_status ?? user.userStatus;
  if (uStatus !== undefined && uStatus !== null) {
    const us = String(uStatus).trim().toLowerCase();
    if (us === "0" || us === "inactive" || us === "disabled" || us === "deleted") return false;
    if (us === "1" || us === "active") return true;
  }

  if (user.status !== undefined && user.status !== null) {
    const st = String(user.status).trim().toLowerCase();
    if (st === "0" || st === "inactive" || st === "disabled" || st === "deleted") return false;
    if (st === "1" || st === "active") return true;
  }

  const act = user.is_active ?? user.isActive;
  if (act !== undefined && act !== null) {
    if (act === 0 || act === "0" || act === false) return false;
    if (act === 1 || act === "1" || act === true) return true;
  }

  return true;
};

export const isUserActiveFromList = (userId, userName, allUsers = []) => {
  if (!Array.isArray(allUsers) || allUsers.length === 0) return true;

  const targetIdStr = userId !== null && userId !== undefined ? String(userId).trim() : null;
  const targetNameNorm = userName ? String(userName).trim().toLowerCase() : null;

  const foundUser = allUsers.find((u) => {
    if (!u) return false;
    const uIdStr = u.id !== undefined && u.id !== null ? String(u.id).trim() : (u.user_id !== undefined && u.user_id !== null ? String(u.user_id).trim() : null);
    if (targetIdStr && uIdStr && targetIdStr === uIdStr) {
      return true;
    }
    if (targetNameNorm) {
      const fn = (u.firstName || u.first_name || "").trim().toLowerCase();
      const ln = (u.lastName || u.last_name || "").trim().toLowerCase();
      const fullName = `${fn} ${ln}`.trim();
      const name = (u.name || u.userName || u.user_name || "").trim().toLowerCase();
      if (fullName && (fullName === targetNameNorm || targetNameNorm.includes(fullName) || fullName.includes(targetNameNorm))) {
        return true;
      }
      if (name && (name === targetNameNorm || targetNameNorm.includes(name) || name.includes(targetNameNorm))) {
        return true;
      }
    }
    return false;
  });

  if (foundUser) {
    return isUserActive(foundUser);
  }

  return true;
};


